import { useEffect, useState, useMemo } from "react";
import { Col, Row, Card } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  datetimeFormats,
  inspectionTypes,
  machineExpiredStatus,
} from "../../constants/constants";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import { getDueInvoices } from "../../services/dashboard";
import moment from "moment";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import BlackPinIcon from "assets/images/inspector-pin-icon.svg";
import ImageTag from "components/common/ImageTag";
import DownloadIocon from "assets/images/download-icon.svg";
import { downloadInvoiceApiCall } from "services/invoice";
import { nameBasedProtectedRoutes } from "router/protected";
import { toast } from "react-toastify";

/**
 * Create a custom component which will render upcoming inspection list section in dashboard.
 * @param props
 * @returns Upcoming Inspection component
 */
export const DueInvoices = (props: any) => {
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon
  const navigate = useNavigate();

  useEffect(() => {
    fetchDueInvoices({
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      search: props.search,
    });
  }, [sorting, props.search]);

  const fetchDueInvoices = (params: any) => {
    setLoading(true);
    getDueInvoices(
      {
        ...params,
      },
      props.userId
    ).then((response) => {
      setData(response?.data?.resultRows);
      setLoading(false);
    });
  };

  const downloadInvoice = (id: number) => {
    downloadInvoiceApiCall({ id })
      .then((response) => {
        
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const viewSR = (id: number) => {
    navigate(nameBasedProtectedRoutes.inspectionDetail.path + "/" + id);
  };

  /* Column Definition requires for MaterialReactTable */
  type DueInvoice = {
    id: number;
    amount: string;
    days_overdue: string;
    invoice_file: number;
    Inspection: {
      id: number;
      address: string;
      city: string;
      inspection_date: string;
      job_time: string;
      zipcode: string;
      sr_number: string;
      state: {
        title: string;
        code: string;
      };
    };
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<DueInvoice>[]>(
    () => [
      {
        accessorKey: "Inspection.sr_number",
        header: "SR NO.",
        Cell: ({ cell, row }) => (
          <Link
            to={`${nameBasedProtectedRoutes.inspectionDetail.path}/${row.original?.Inspection?.id}`}
            className="text-decoration-none text-link"
          >
            <strong>{cell.getValue<string>()}</strong>
          </Link>
        ),
      },
      {
        accessorKey: "Inspection.address",
        header: "LOCATION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const inspectionData = row.original.Inspection;
          const city = inspectionData?.city ? ", " + inspectionData?.city : "";
          const state = inspectionData?.state
            ? ", " + inspectionData?.state?.title
            : "";
          const zip = inspectionData?.zipcode
            ? ", " + inspectionData?.zipcode
            : "";
          const fullAddress = inspectionData.address + city + state + zip;
          return inspectionData.address ? fullAddress : "-";
        },
      },
      {
        accessorKey: "amount",
        header: "AMOUNT",
        Cell: ({ cell, row }) => {
          return row.original?.amount ? `$${row.original?.amount}` : "-";
        },
      },
      {
        accessorKey: "Inspection.job_time",
        header: "INSPECTION DATE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection?.inspection_date
            ? moment(
                `${row.original?.Inspection?.inspection_date} ${row.original?.Inspection?.job_time}`
              ).format(datetimeFormats.separatedDateTime)
            : "-";
        },
      },
      {
        accessorKey: "days_overdue",
        header: "DAYS PAST DUE",
      },
      {
        // accessorKey: "id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          return (
            <>
              <OverlayTrigger overlay={<Tooltip>Download Invoice</Tooltip>}>
                <span
                  role="button"
                  onClick={() => downloadInvoice(row.original?.id)}
                  className="d-inline-block"
                >
                  <ImageTag src={DownloadIocon} alt="Download Invoice" />
                </span>
              </OverlayTrigger>
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <Row className="mb-4">
      <Col md={12}>
        <Card className="border-0">
          <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11">
            INVOICES DUE
          </Card.Header>
          <Card.Body className="pe-4 ps-4 pt-4">
            <div className="table-responsive dashboard-table-data">
              {data.length > 0 ? (
                <MaterialReactTable
                  muiTableProps={{
                    classes: {
                      root: "table custom-table border-none-table table-striped machine-custome-table",
                    },
                  }}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  enableHiding={false}
                  enableColumnFilters={false}
                  enablePagination={false}
                  onSortingChange={setSorting}
                  manualSorting
                  columns={tableColumns}
                  data={data}
                  state={{ sorting }}
                />
              ) : (
                <p className="text-center text-muted fst-italic mb-0 py-3">
                  No record to display
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
